import React, { useRef, useState, useEffect, ReactNode } from "react";
import { useReactMediaRecorder } from "react-media-recorder";
import { Button, ModalDialog, Loader } from "@culturehq/components";

import { useConfirmClose, useDoor, useIsMobile } from "app/hooks";
import { errorService, t } from "app/utils";
import { RECORDER_CUSTOMER_ID } from "app/utils/constants";
import { SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";
import { HandleMediaChange } from "components/stories/post-stories-v2/types";
import { Organization } from "chq";
import Prompter from "../Prompter/Prompter";
import ChqModalDialog from "../ChqModalDialog";
import CameraRecorderEffects from "../CameraRecorderEffects";
import GetVideoThumbnail from "../GetVideoThumbnail";
import CameraButtons from "./CameraButtons";
import CameraCountdown from "./CameraCountdown";
import CameraPreview from "./CameraPreview";
import RecorderOptions from "./RecorderOptions";

const INITIAL_COUNTDOWN_VALUE = 3;

const PERMISSION_ERROR = "permission_denied";

declare global {
  interface Window {
    tsvb: any;
  }
}

export type ManualStatus = "recording" | "stopped" | "paused" | "idle";
export type CurrentOption = "none" | "video" | "audio" | "screen" | "backgrounds" | "layout" | "effects" | "prompter";

interface EffectsSDKCameraRecorderProps {
  organization: Organization;
  className?: string;
  initialOpen?: boolean;
  onVideoRecorded: (file: HandleMediaChange) => Promise<void>;
  script?: string;
  children?: ReactNode;
  onOpen?: () => void;
}

const EffectsSDKCameraRecorder: React.FC<EffectsSDKCameraRecorderProps> = ({
  organization,
  children,
  className,
  initialOpen = false,
  script = undefined,
  onVideoRecorded,
  onOpen: onParentOpen
}) => {
  const { open, onOpen, onClose } = useDoor(initialOpen);
  const [audioDevices, setAudioDevices] = useState<{ label: any; value: any }[]>([]);
  const [videoDevices, setVideoDevices] = useState<{ label: any; value: any }[]>([]);
  const [askingPermission, setAskingPermission] = useState(false);
  const [videoConstraints, setVideoConstraints] = useState<any>(false);
  const [audioConstraints, setAudioConstraints] = useState<any>(false);
  const [videoEditorOpen, setVideoEditorOpen] = useState(false);
  const [screenRecord, setScreenRecord] = useState(false);
  const [manualStatus, setManualStatus] = useState<ManualStatus>("idle");
  const [persmissionRequested, setPermissionsRequested] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [selectedAudioDeviceState, setSelectedAudioDeviceState] = useState<any>();
  const [selectedVideoDeviceState, setSelectedVideoDeviceState] = useState<
    string | SelectValueWithCategory | null
  >(null);
  const [showScript, setShowScript] = useState(false);
  const [recordEffects, setRecordEffects] = useState(false);
  const [mediaBlob, setMediaBlob] = useState<string | undefined>(undefined);
  const [effectsIsReady, setEffectsIsReady] = useState(false);
  const [editableScript, setEditableScript] = useState(script || "");
  const hasCamera = !!videoDevices.length;

  const [countdown, setCountdown] = useState<number | undefined>();
  const [showCountdown, setShowCountdown] = useState(false);
  const [prompterWasOpen, setPrompterWasOpen] = useState(false);
  const [retake, setRetake] = useState(false);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);

  const recorderPreview = useReactMediaRecorder({
    screen: screenRecord,
    video: videoConstraints,
    audio: audioConstraints
  });

  const {
    status,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    previewStream,
    previewAudioStream,
    mediaBlobUrl,
    clearBlobUrl,
    error
  } = recorderPreview;
  const [effectsSDK, setEffectsSDK] = useState<any>(null);
  const [currentOption, setCurrentOption] = useState<CurrentOption>("none");

  const isMobile = useIsMobile();
  const blobToUse = screenRecord ? mediaBlobUrl : mediaBlob;

  const changeIntercomDisplay = (hide: boolean) => {
    const intercomElement = document.getElementsByClassName("intercom-lightweight-app")[0] as HTMLElement;
    if (intercomElement?.style) {
      intercomElement.style.display = hide ? "none" : "block";
    }
  };

  // hide the intercom button
  useEffect(() => {
    if (open && isMobile) {
      changeIntercomDisplay(true);
    }

    return () => {
      changeIntercomDisplay(false);
    };
  }, [isMobile, open]);

  useEffect(() => {
    if (showScript && !screenRecord && countdown !== undefined) {
      setShowScript(false);
      setPrompterWasOpen(true);
    }
    setCurrentOption("none");
    const isCountdownChecked = (countdown || 0) > 0 && (countdown || 0) <= INITIAL_COUNTDOWN_VALUE;
    const timeoutId = isCountdownChecked
      ? setTimeout(() => setCountdown((countdown || 0) - 1), 1000) : null;
    if (countdown === 0) {
      handleStartRecord();
      setCountdown(undefined);
      setShowCountdown(false);
      if (prompterWasOpen) {
        setShowScript(true);
        setPrompterWasOpen(false);
      }
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    if (!open || !onParentOpen) return;

    onParentOpen();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    // eslint-disable-next-line new-cap
    setEffectsSDK(new window.tsvb(RECORDER_CUSTOMER_ID));
  }, []);

  useEffect(() => {
    if (error === PERMISSION_ERROR && !screenRecord) {
      onCloseModal();
    }

    if (error === PERMISSION_ERROR && screenRecord) {
      setVideoConstraints({ width: window.screen.width, height: window.screen.height });
      setScreenRecord(false);
      setManualStatus("idle");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (effectsSDK) {
      effectsSDK.config({
        provider: "webgpu"
      });
      effectsSDK.onReady = () => {
        setEffectsIsReady(true);
        effectsSDK.run();
        if (screenRecord) {
          effectsSDK.disableMirroring();
        } else {
          effectsSDK.enableMirroring();
        }
      };
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [effectsSDK, manualStatus]);

  useEffect(() => {
    if (!askingPermission && status === "acquiring_media" && !persmissionRequested) {
      setAskingPermission(true);
    }

    if (askingPermission && status === "recording" && !persmissionRequested) {
      setPermissionsRequested(true);
      getDevices();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askingPermission, open, persmissionRequested, status]);

  useEffect(() => {
    const onEndedEventListener = () => {
      stopRecording();
      setMediaBlob(mediaBlobUrl);
      setTimeout(() => {
        setVideoEditorOpen(true);
      }, 1000);
    };
    const isVideoTrackRecording = screenRecord && status === "recording";
    const videoTrack = isVideoTrackRecording && previewStream?.getVideoTracks()?.[0];
    if (videoTrack) videoTrack.addEventListener("ended", onEndedEventListener);

    return () => {
      if (videoTrack) videoTrack.removeEventListener("ended", onEndedEventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecord, status]);

  useEffect(() => {
    if (!hasCamera || audioDevices.length === 0) getDevices();
    if (open) {
      stopRecording();
      clearBlobUrl();
      startRecording();

      if (screenRecord) {
        setManualStatus("recording");
        setCurrentOption("none");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenRecord, open]);

  useEffect(() => {
    if (videoRef.current && previewStream && effectsSDK && hasCamera) {
      if (!videoRef.current.srcObject) {
        if (!retake) {
          effectsSDK.clear();
          effectsSDK.useStream(previewStream);
        }

        videoRef.current.srcObject = effectsSDK.getStream();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectsSDK, previewStream, hasCamera]);

  useEffect(() => {
    if (open && videoConstraints.deviceId && audioConstraints.deviceId) {
      startRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoConstraints.deviceId, audioConstraints.deviceId]);

  useEffect(() => {
    let isMounted = true;
    const getDevicesByKind = (devices: any[], kind: string) => devices
      .filter((device: { kind: any }) => device.kind === kind)
      .map(({ label, deviceId }) => ({ label, value: deviceId }));

    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      if (!isMounted) return;
      setAudioDevices(getDevicesByKind(devices, "audioinput"));
      setVideoDevices(getDevicesByKind(devices, "videoinput"));
    };

    getDevices();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setVideoConstraints(
      hasCamera && {
        deviceId: videoDevices[0].value,
        width: { max: 1920, min: 1080 },
        height: { max: 1080, min: 720 }
      }
    );
    setSelectedVideoDeviceState(videoDevices[0]?.value);
  }, [videoDevices, hasCamera]);

  useEffect(() => {
    setAudioConstraints(
      audioDevices.length > 0 && { deviceId: audioDevices[0].value }
    );
    setSelectedAudioDeviceState(audioDevices[0]?.value);
  }, [audioDevices]);

  const handleSelectRecordMethod = (value: string | SelectValueWithCategory | null) => {
    if (isDisableOption("screen")) return;

    setVideoConstraints({ width: window.screen.width, height: window.screen.height });
    setScreenRecord(value === "screen");
  };

  const handleSelectedAudioDevice = (value: string | SelectValueWithCategory | null) => {
    if (value === selectedAudioDeviceState) return;

    setAudioConstraints({ deviceId: value });
    setSelectedAudioDeviceState(value);
  };

  const onCloseModal = () => {
    onClose();
    resetState();
    stopRecording();
  };

  const { onConfirmClose } = useConfirmClose(
    t("You are about to close the recording modal. Any unsaved video will be lost. Are you sure you want to proceed?"),
    onCloseModal,
    true
  );

  const handleStartCountdown = () => {
    setShowCountdown(true);
    setCountdown(INITIAL_COUNTDOWN_VALUE);
  };

  const handleStartRecord = () => {
    setCurrentOption("none");
    setRecordEffects(true);

    if (videoRef.current) {
      videoRef.current.srcObject = effectsSDK.getStream();
      setManualStatus("recording");
    }
  };

  const handleRetake = () => {
    setRecordEffects(false);
    setRetake(true);
    setManualStatus("idle");
    setShowCountdown(false);
  };

  const resetState = () => {
    setEditableScript(script || "");
    setAudioDevices([]);
    setVideoDevices([]);
    setAskingPermission(false);
    setVideoConstraints(false);
    setAudioConstraints(false);
    setVideoEditorOpen(false);
    setScreenRecord(false);
    setManualStatus("idle");
    setPermissionsRequested(false);
    setSelectedAudioDeviceState(audioDevices[0]?.label);
    setSelectedVideoDeviceState(videoDevices[0]?.label);
    setShowScript(false);
    setRecordEffects(false);
    setMediaBlob(undefined);
    setCurrentOption("none");
    setRetake(false);
    setEffectsIsReady(false);
  };

  const handleFinishRecord = () => {
    if (screenRecord) {
      stopRecording();
      setMediaBlob(mediaBlobUrl);
      setTimeout(() => {
        setVideoEditorOpen(true);
      }, 1000);
    }
    setManualStatus("stopped");
  };

  const handleVideoEdited = async (thumb: Blob) => {
    if (screenRecord && !mediaBlobUrl) return;
    if (!screenRecord && !mediaBlob) return;
    if (!blobToUse) {
      errorService.error("No blob to use", { screenRecord, mediaBlob, mediaBlobUrl });
      return;
    }

    setVideoEditorOpen(false);
    const videoBlob = await fetch(blobToUse).then(file => file.blob());
    const duration: number = await new Promise(resolve => {
      const video = document.createElement("video");

      video.onloadedmetadata = () => {
        if (video.duration === Infinity) {
          video.currentTime = Number.MAX_SAFE_INTEGER;
          video.ontimeupdate = () => {
            video.ontimeupdate = null;
            video.currentTime = 0;
            resolve(video.duration);
            URL.revokeObjectURL(video.src);
          };
        } else {
          resolve(video.duration);
          URL.revokeObjectURL(video.src);
        }
      };

      video.src = URL.createObjectURL(videoBlob);
    });
    onVideoRecorded({ media: videoBlob, thumb, isRecordedVideo: true, duration });
    onCloseModal();
    clearBlobUrl();
  };

  const handlePause = () => {
    setManualStatus("paused");
    pauseRecording();
  };

  const handleResume = () => {
    setManualStatus("recording");
    resumeRecording();
  };

  const handleSelectOption = (option: "none" | "video" | "audio" | "screen" | "backgrounds" | "layout" | "effects" | "prompter") => {
    const isDisable = isDisableOption(option);
    if (isDisable) {
      setCurrentOption("none");
      return;
    }
    if ((screenRecord && !(manualStatus === "recording")) || manualStatus === "recording") {
      if (screenRecord && !(manualStatus === "recording") && option === "screen") {
        setCurrentOption(prev => (prev !== "none" && prev !== "prompter" ? "none" : option));
      }

      if (manualStatus === "recording" && !screenRecord) {
        setCurrentOption(prev => (prev !== "none" && prev !== "prompter" ? "none" : option));
      }
    } else {
      setCurrentOption(prev => (prev !== "none" && prev !== "prompter" ? "none" : option));
    }
  };

  const isDisableOption = (option: string) => {
    if (option === "prompter") {
      return !effectsIsReady || isNotPrompter(currentOption);
    }
    if (screenRecord) {
      return true;
    }

    if ((option === "backgrounds" || option === "layout" || option === "effects") && !effectsIsReady) {
      return true;
    }

    if (option === "video" && !hasCamera) {
      return true;
    }

    return isNotPrompter(currentOption) && currentOption !== option;
  };

  const onShowScriptChange = (value: string | SelectValueWithCategory | null) => {
    if (!effectsIsReady) return;
    setCurrentOption(prev => {
      const notPrompter = isNotPrompter(prev);
      setShowScript(notPrompter ? false : value === "yes");
      if (notPrompter) {
        return "none";
      }

      return (value === "yes" ? "prompter" : "none");
    });
  };

  const isNotPrompter = (value: string) => value !== "none" && value !== "prompter";

  const handleSelectedVideoDevice = (value: string | SelectValueWithCategory | null) => {
    if (value === selectedVideoDeviceState) return;

    stopRecording();
    setVideoConstraints({
      deviceId: value,
      width: { max: 1920, min: 1080 },
      height: { max: 1080, min: 720 }
    });
    setSelectedVideoDeviceState(value);
  };

  const addAudioToTheTrack = () => {
    if (effectsSDK.getStream().getTracks().length === 1) {
      effectsSDK.getStream().addTrack(previewAudioStream?.getTracks()[0]);
    }
    return effectsSDK.getStream();
  };

  const getDevices = async () => {
    const getDevicesByKind = (devices: any[], kind: string) => devices
      .filter((device: { kind: any }) => device.kind === kind)
      .map(({ label, deviceId }) => ({ label, value: deviceId }));

    const devices = await navigator.mediaDevices.enumerateDevices();
    if (!open) return;
    setAudioDevices(getDevicesByKind(devices, "audioinput"));
    setVideoDevices(getDevicesByKind(devices, "videoinput"));
  };

  const handleFinishEffectsSDKRecord = (blobUrl: string) => {
    setMediaBlob(blobUrl);
    setVideoEditorOpen(true);
  };

  const handleVideoProcessing = (processing: boolean) => {
    setIsProcessingVideo(processing);
  };

  return (
    <>
      {open && (
        <ChqModalDialog entrance="zoomIn" onClose={onConfirmClose} className="camera-recorder-modal camera-recorder-modal-full camera-recorder-modal-background" ariaLabelledBy="camera-recorder-title" ariaDescribedby="camera-recorder-title" style={{ overlay: { backgroundColor: "#000000" } }}>
          <ModalDialog.Body>
            <div className="Video-Preview__Container">
              {!videoEditorOpen && (status === "recording" || status === "paused") && (
                <>
                  <CameraButtons
                    effectsIsReady={effectsIsReady}
                    manualStatus={manualStatus}
                    onConfirmClose={onConfirmClose}
                    onFinishRecord={handleFinishRecord}
                    onRetake={handleRetake}
                    onPause={handlePause}
                    onResume={handleResume}
                    onStartCountdown={handleStartCountdown}
                    recordEffects={recordEffects}
                    screenRecord={screenRecord}
                    showCountdown={showCountdown}
                  />
                  {showCountdown && !showScript && <CameraCountdown countdown={countdown} />}
                  {recordEffects && (
                    <CameraRecorderEffects
                      customMediaStream={addAudioToTheTrack()}
                      videoConstraints={videoConstraints}
                      audioConstraints={audioConstraints}
                      status={manualStatus}
                      onFinishRecord={handleFinishEffectsSDKRecord}
                      onClick={() => { setCurrentOption("none"); }}
                    />
                  )}
                  <CameraPreview
                    effectsIsReady={effectsIsReady}
                    hasCamera={hasCamera}
                    onSetCurrentOption={setCurrentOption}
                    recordEffects={recordEffects}
                    showCountdown={showCountdown}
                    videoRef={videoRef}
                  />
                  <>
                    {!screenRecord && (
                      <Prompter
                        editableScript={editableScript}
                        setEditableScript={setEditableScript}
                        setShowScript={setShowScript}
                        showScript={showScript}
                      />
                    )}
                  </>
                  <RecorderOptions
                    audioDevices={audioDevices}
                    currentOption={currentOption}
                    effectsSDK={effectsSDK}
                    isDisableOption={isDisableOption}
                    manualStatus={manualStatus}
                    onFinishRecord={handleFinishRecord}
                    onPause={handlePause}
                    onResume={handleResume}
                    onSelectOption={handleSelectOption}
                    onSelectedAudioDevice={handleSelectedAudioDevice}
                    onSelectedVideoDevice={handleSelectedVideoDevice}
                    onSelectRecordMethod={handleSelectRecordMethod}
                    onShowScriptChange={onShowScriptChange}
                    onStartCountdown={handleStartCountdown}
                    open={open}
                    organization={organization}
                    screenRecord={screenRecord}
                    selectedAudioDeviceState={selectedAudioDeviceState}
                    selectedVideoDeviceState={selectedVideoDeviceState}
                    showScript={showScript}
                    videoDevices={videoDevices}
                  />
                </>
              )}
              {videoEditorOpen && blobToUse && (
                <GetVideoThumbnail
                  video={blobToUse}
                  onEdit={handleVideoEdited}
                  onProcessing={handleVideoProcessing}
                />
              )}
              {!screenRecord && <Loader loading={status !== "recording" && status !== "paused" && !videoEditorOpen && !isProcessingVideo} />}
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
      <Button className={className} onClick={onOpen}>
        {children}
      </Button>
    </>
  );
};

export default EffectsSDKCameraRecorder;
